import { restartableTask, timeout } from 'ember-concurrency';
import IntlService from 'ember-intl/services/intl';
import moment from 'moment-timezone';
import SkuModel from 'volta/models/sku';
import Transaction from 'volta/models/transaction';
import StoreService from 'volta/services/-store';
import BvFlashService from 'volta/services/bv-flash';
import BvMetricsService from 'volta/services/bv-metrics';
import { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler, TError } from 'volta/utils/error-utils';
import { eqFilter, lkFilter, niFilter } from 'volta/utils/filters-utils';
import { btnGroupOptions } from 'volta/utils/style-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isBlank } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

export interface ITransactionCPParams {
  mode: 'cumulativeMode' | 'fillOnly' | 'replaceMode';
  startDate?: Date;
}

export interface ITransactionSuccessResult {
  nbCopied: number;
  nbDeleted: number;
}

export const WAREHOUSE_ITEM_SEARCH_QUERY: IJsonApiQuery = {
  sort: 'partCode',
  page: {
    offset: 0,
    limit: 50
  }
};

export interface IAbstractTransactionsCopierArgs {
  itemSearchQueryParams: IJsonApiQuery;
  selectedWarehouseId?: string;
  skuId?: string;
  onClose?: (data?: unknown) => void;
}

export default class AbstractTransactionsCopier<
  T extends IAbstractTransactionsCopierArgs
> extends Component<T> {
  @service store!: StoreService;
  @service intl!: IntlService;
  @service bvMetrics!: BvMetricsService;
  @service bvFlash!: BvFlashService;

  @tracked itemSearchQueryParams: IJsonApiQuery = WAREHOUSE_ITEM_SEARCH_QUERY;
  @tracked selectedWarehouseId?: string = this.args.selectedWarehouseId;
  @tracked searchExcludeSkuIds: string[] = [];
  @tracked params: ITransactionCPParams = {
    mode: 'cumulativeMode'
  }; // stores the parameters

  modeOptions = btnGroupOptions({
    cumulativeMode: 'skuModal.transactions.cumulativeMode',
    fillOnly: 'skuModal.transactions.fillOnly',
    replaceMode: 'skuModal.transactions.replaceMode'
  });

  get skuId(): string | undefined {
    return this.args.skuId;
  }

  get selectedItems(): SkuModel[] {
    return [];
  }

  get disableCopyTransactionsButton() {
    const { selectedItems, skuId } = this;
    const { startDate } = this.params;

    if (!startDate) {
      return true;
    }

    if (!selectedItems.length) {
      return true;
    }

    if (selectedItems.findBy('id', skuId)) {
      /* trying to copy to itself */ return true;
    }

    return false;
  }

  @action
  addStartDate(date: Date) {
    this.bvMetrics.trackAction(this, 'setStartDate');
    this.params = { ...this.params, startDate: date };
  }

  @action
  selectWarehouse(newWarehouseId: string) {
    this.bvMetrics.trackAction(this, 'selectWarehouse');
    this.selectedWarehouseId = newWarehouseId;
  }

  searchItems = restartableTask(async (term: string) => {
    await timeout(600);
    const { selectedWarehouseId } = this;
    if (isBlank(term)) {
      return [];
    }

    const query = this.itemSearchQueryParams ?? {};

    query.filter = {
      q: lkFilter(term)
    };

    if (selectedWarehouseId) {
      query.filter.warehouseId = eqFilter(selectedWarehouseId);
    }

    const excluded = this._getExcludeList();
    if (excluded && excluded.length) {
      query.filter.id = niFilter(excluded);
    }
    try {
      const data = await this.store.normalPost('skus/list', query);
      const skus = normalize('sku', data) as SkuModel[];
      return skus;
    } catch (err) {
      return defaultErrorHandler(err);
    }
  });

  copyTransactions = restartableTask(async (entityId: string, targets: string[]) => {
    this.bvMetrics.trackAction(this, 'copyTransactions');

    const { mode, startDate } = this.params;
    const from = moment.utc(startDate).format('YYYY-MM-DD');

    const cmd = {
      entityId,
      replaceMode: mode === 'cumulativeMode' ? null : mode === 'replaceMode',
      from,
      targets
    };

    try {
      const copied = await Transaction.copyTransactionsTo(cmd);
      this._onCopyTransactionsSuccess(copied);
    } catch (err) {
      this._onDefaultError(err);
    }
  });

  // Helper Functions
  // ~~~~~

  _onCopyTransactionsSuccess({ data }: { data: ITransactionSuccessResult[] }) {
    const nbDeleted = data.reduce((_memo, el: ITransactionSuccessResult) => el.nbDeleted, 0);
    const nbCopied = data.reduce((_memo, el: ITransactionSuccessResult) => el.nbCopied, 0);
    const suffix = nbDeleted ? 'WithDeleted' : '';
    this.bvFlash.success(
      this.intl.t(`skuModal.transactions.copySuccess${suffix}`, { nbDeleted, nbCopied })
    );
    this.args.onClose?.(data);
    return data;
  }

  _onDefaultError(reason: TError) {
    defaultErrorHandler(reason);
    this.bvFlash.apiError(reason);
  }

  protected _getExcludeList() {
    const { selectedItems = [], searchExcludeSkuIds = [] } = this;

    if (!selectedItems?.length && !searchExcludeSkuIds?.length) {
      return undefined;
    }
    // call mapBy on selectedItems only if present
    return (
      selectedItems ? searchExcludeSkuIds.concat(selectedItems.mapBy('id')) : searchExcludeSkuIds
    ).filter(Boolean);
  }
}
